import React, { useEffect, useRef, useState } from "react"
import { Filter, RefreshCw } from "lucide-react"
import Button from "../common/Button.jsx"
import { SearchBar } from "../filters/index.js"

const SEARCH_MIN_WIDTH = 400
const SEARCH_MIN_WIDTH_PX = `${SEARCH_MIN_WIDTH}px`

export default function OrdersFilters({
    showAllLocationsBadge = true,
    showCurrentYearFilter = false,
    currentYearLabel = "",
    searchChips = [],
    searchFreeText = "",
    onSearch,
    onRegisterAddChip,
    onRefresh,
    onToggleAllLocations,
    onToggleLast7Days,
    onToggleToday,
    onToggleCurrentYear,
    onOpenCustomDateRange,
    disableOfflineDatePresets = false,
    isAllLocations,
    isLast7Days,
    isToday,
    isCurrentYear,
    isCustomDateRange,
}) {
    const rootRef = useRef(/** @type {HTMLDivElement | null} */(null))
    const buttonsMeasureRef = useRef(/** @type {HTMLDivElement | null} */(null))
    const searchBarRef = useRef(/** @type {{ submit?: () => void } | null} */(null))
    const [isSplitRows, setIsSplitRows] = useState(false)

    useEffect(() => {
        if (!rootRef.current || !buttonsMeasureRef.current) {
            return
        }

        function syncLayoutMode() {
            if (!rootRef.current || !buttonsMeasureRef.current) {
                return
            }

            const rootWidth = rootRef.current.clientWidth
            const buttonsWidth = buttonsMeasureRef.current.scrollWidth
            const requiredSingleRowWidth = SEARCH_MIN_WIDTH + buttonsWidth + 8
            const next = rootWidth < requiredSingleRowWidth
            setIsSplitRows((prev) => (prev === next ? prev : next))
        }

        syncLayoutMode()
        const observer = new ResizeObserver(syncLayoutMode)
        observer.observe(rootRef.current)
        observer.observe(buttonsMeasureRef.current)
        window.addEventListener("resize", syncLayoutMode)

        return () => {
            observer.disconnect()
            window.removeEventListener("resize", syncLayoutMode)
        }
    }, [showAllLocationsBadge])

    function handleFormSubmit(event) {
        event.preventDefault()
        searchBarRef.current?.submit?.()
    }

    return (
        <div
            ref={rootRef}
            className={
                "mb-4 d-flex gap-2 position-relative min-w-0 " +
                (isSplitRows ? "flex-column" : "flex-nowrap align-items-stretch")
            }
        >
            <form
                className="position-relative"
                style={{
                    minWidth: isSplitRows ? 0 : SEARCH_MIN_WIDTH_PX,
                    width: isSplitRows ? "100%" : "auto",
                    flex: isSplitRows ? "none" : "1 1 auto",
                }}
                onSubmit={handleFormSubmit}
            >
                <SearchBar
                    ref={searchBarRef}
                    placeholder="Search orders..."
                    initialChips={searchChips}
                    initialFreeText={searchFreeText}
                    onSearch={onSearch}
                    onAddChip={onRegisterAddChip}
                />
            </form>
            <div
                className={
                    "d-flex gap-2 " +
                    (isSplitRows ?
                        "flex-wrap w-100" :
                        "flex-nowrap flex-shrink-0 align-items-stretch")
                }
            >
                <Button
                    variant="outline"
                    size="sm"
                    className={isSplitRows ? "" : "h-100"}
                    onClick={onRefresh}
                >
                    <span className="me-2"><RefreshCw size={16} /></span>
                    Refresh
                </Button>
                {showAllLocationsBadge ? (
                    <Button
                        variant={isAllLocations ? "secondary" : "outline"}
                        size="sm"
                        className={isSplitRows ? "" : "h-100"}
                        onClick={onToggleAllLocations}
                    >
                        <span className="me-2"><Filter size={16} /></span>
                        All locations
                    </Button>
                ) : null}
                <Button
                    variant={isLast7Days ? "secondary" : "outline"}
                    size="sm"
                    className={isSplitRows ? "" : "h-100"}
                    disabled={disableOfflineDatePresets}
                    style={disableOfflineDatePresets ? { cursor: "default" } : undefined}
                    onClick={onToggleLast7Days}
                >
                    Last 7 days
                </Button>
                <Button
                    variant={isToday ? "secondary" : "outline"}
                    size="sm"
                    className={isSplitRows ? "" : "h-100"}
                    disabled={disableOfflineDatePresets}
                    style={disableOfflineDatePresets ? { cursor: "default" } : undefined}
                    onClick={onToggleToday}
                >
                    Today
                </Button>
                <Button
                    variant={isCustomDateRange ? "secondary" : "outline"}
                    size="sm"
                    className={isSplitRows ? "" : "h-100"}
                    disabled={disableOfflineDatePresets}
                    style={disableOfflineDatePresets ? { cursor: "default" } : undefined}
                    onClick={onOpenCustomDateRange}
                >
                    Custom Date Range
                </Button>
                {showCurrentYearFilter ? (
                    <Button
                        variant={isCurrentYear ? "secondary" : "outline"}
                        size="sm"
                        className={isSplitRows ? "" : "h-100"}
                        onClick={onToggleCurrentYear}
                    >
                        {currentYearLabel}
                    </Button>
                ) : null}
            </div>

            <div
                ref={buttonsMeasureRef}
                className="d-flex gap-2 flex-nowrap"
                style={{
                    position: "fixed",
                    left: "-10000px",
                    top: 0,
                    visibility: "hidden",
                    pointerEvents: "none",
                }}
                aria-hidden="true"
            >
                <Button variant="outline" size="sm">
                    <span className="me-2"><RefreshCw size={16} /></span>
                    Refresh
                </Button>
                {showAllLocationsBadge ? (
                    <Button variant="outline" size="sm">
                        <span className="me-2"><Filter size={16} /></span>
                        All locations
                    </Button>
                ) : null}
                <Button variant="outline" size="sm">Last 7 days</Button>
                <Button variant="outline" size="sm">Today</Button>
                <Button variant="outline" size="sm">Custom Date Range</Button>
                {showCurrentYearFilter ? (
                    <Button variant="outline" size="sm">{currentYearLabel}</Button>
                ) : null}
            </div>
        </div>
    )
}